import {
	type CollectionJSONObject,
	type CollectionSelector,
	type CollectionSummaryJSONObject,
	type DepartmentJSONObject,
	type DuplicateNotification,
	type DuplicateTaskResult,
	isDuplicateNotification,
	isDuplicateTaskResult,
	LongRunningTaskStatus,
	type Modifications,
} from '@ikegps/core-api';
import { isSystemTag } from '@ikegps/core-api';
import {
	CollectionModel,
	CollectionModelFieldImage,
	CollectionModelFieldTrueSize,
	IkeArray,
	IkeModel,
} from '@ikegps/core-shared';
import { getLongRunningTaskManager } from '../modules/long.running.task.manager';
import { CurrentState } from '../state/page.state';
import { AppStore } from '../store';
import { handleGenericError, type IkeClientService, Services } from './index';

/** Terminal-state outcome of a collection-duplicate task. */
export interface DuplicateOutcome {
	status: LongRunningTaskStatus;
	notifications: DuplicateNotification[];
	error?: string;
	result?: DuplicateTaskResult;
}

export class CollectionService implements IkeClientService {
	__priority = 100;

	static readonly BATCH_SIZE = 50;

	getFirstCaptureId(collection: CollectionModel): string | null {
		if (collection == null) {
			return null;
		}

		const captureIds = this.getCaptureIds(collection);
		return captureIds[0];
	}

	getCaptureIds(collection: CollectionModel): string[] {
		const captureIds: string[] = [];
		collection.forEachField((collectionField) => {
			if (
				CollectionModelFieldTrueSize.isTrueSizeField(collectionField) ||
				CollectionModelFieldImage.isImageField(collectionField)
			) {
				for (const capture of collectionField.getCaptures()) {
					captureIds.push(capture.objectId);
				}
			}

			return true;
		});

		return captureIds;
	}

	async remove(collectionSummaries: CollectionSummaryJSONObject[]): Promise<any> {
		if (Services.Department.current == null) {
			throw new Error('Cannot delete collections without a current department');
		}

		const allUuids = collectionSummaries.map((c) => c.uuid);
		const tagIds = this.getTagIds(collectionSummaries);

		try {
			const startTime = Date.now();

			// Batch deletions to avoid hitting server limit
			const uuidBatches = IkeArray.chunk(allUuids, CollectionService.BATCH_SIZE);
			for (const uuids of uuidBatches) {
				await Services.Department.rest.Collection.delete({ uuids });
			}

			await Services.Tag.refresh(tagIds);
			CurrentState.Tag.reset();
			CurrentState.setInspectedCollection(null);

			const label = `Tags: ${tagIds.length}, Collections: ${collectionSummaries.length}`;
			Services.Analytics.trackEvent(
				Services.Analytics.EventCategory.BigMap,
				'COLLECTION_DELETE',
				label,
				Date.now() - startTime
			);
		} catch (e) {
			handleGenericError(e);
		}
	}

	/**
	 * Duplicate the given collections into a new tag (optionally cross-department).
	 *
	 * Server-side handled via a long-running `task.create` ('duplicate'). Modal callers wait on
	 * the returned promise — it resolves when the task reaches a terminal state and exposes any
	 * per-pole notifications so the caller can present a follow-up dialog. Span links between
	 * duplicated poles are rewritten server-side; spans whose partner is not duplicated are
	 * demoted to a manual measurement using the persisted snapshot.
	 */
	async duplicate(
		collectionSummaries: CollectionSummaryJSONObject[],
		tagName: string,
		toDepartmentId?: string
	): Promise<DuplicateOutcome> {
		const currentDepartment = Services.Department.current;
		if (currentDepartment == null) {
			throw new Error('Cannot duplicate collections without a current department');
		}

		let toDepartment: DepartmentJSONObject;
		if (toDepartmentId != null && toDepartmentId !== currentDepartment.objectId) {
			toDepartment = await Services.Department.departments.find((d) => d.objectId === toDepartmentId);
			if (toDepartment == null) {
				throw new Error('Cannot duplicate collections without a destination department');
			}
		}

		const selectors: CollectionSelector[] = collectionSummaries.map((summary) => ({ uuid: summary.uuid }));
		const tagIds = this.getTagIds(collectionSummaries);

		try {
			const startTime = Date.now();

			const taskManager = getLongRunningTaskManager();
			const taskId = await taskManager.service.startDuplicateTask({
				collections: selectors,
				tagName,
				toDepartmentId,
			});
			if (taskId === '') {
				throw new Error('Failed to start duplicate task — no current department');
			}
			const task = await taskManager.service.awaitTaskCompletion(taskId);

			await Services.Tag.refresh(tagIds);
			CurrentState.Tag.reset();
			CurrentState.setInspectedCollection(null);

			const label =
				toDepartment != null ? `"${currentDepartment.displayName}" -> "${toDepartment.displayName}"` : tagName;
			Services.Analytics.trackEvent(
				Services.Analytics.EventCategory.BigMap,
				'COLLECTION_DUPLICATE',
				label,
				Date.now() - startTime
			);

			const duplicateNotifications = (task.notifications ?? []).filter(isDuplicateNotification);
			return {
				status: task.status,
				notifications: duplicateNotifications,
				error: task.error,
				result: task.result != null && isDuplicateTaskResult(task.result) ? task.result : undefined,
			};
		} catch (e) {
			handleGenericError(e);
			return {
				status: LongRunningTaskStatus.FAILED,
				notifications: [],
				error: e instanceof Error ? e.message : undefined,
			};
		}
	}

	async assign(collectionSummaries: CollectionSummaryJSONObject[], userId?: string | null): Promise<void> {
		userId = userId == null ? undefined : userId;

		if (Services.Department.current == null) {
			throw new Error('Cannot assign collections without a current department');
		}

		const uuids = collectionSummaries.map((summary) => summary.uuid);
		const allSelectors: CollectionSelector[] = uuids.map((uuid) => ({ uuid }));
		const tagIds = this.getTagIds(collectionSummaries);

		try {
			const startTime = Date.now();

			// Batch modifications to avoid hitting server limit
			const selectorBatches = IkeArray.chunk(allSelectors, CollectionService.BATCH_SIZE);
			for (const selectors of selectorBatches) {
				await Services.Department.rest.Collection.assign({ userId, collections: selectors });
			}

			await CurrentState.updateInspectedCollection();
			await this.refresh(uuids);

			const label = `Tags: ${tagIds.length}, Collections: ${collectionSummaries.length}`;
			if (userId != null) {
				Services.Analytics.trackEvent(
					Services.Analytics.EventCategory.BigMap,
					'COLLECTION_ASSIGN',
					label,
					Date.now() - startTime
				);
			} else {
				Services.Analytics.trackEvent(
					Services.Analytics.EventCategory.BigMap,
					'COLLECTION_UNASSIGN',
					label,
					Date.now() - startTime
				);
			}
		} catch (e) {
			handleGenericError(e);
		}
	}

	async move(
		collectionSummaries: CollectionSummaryJSONObject[],
		tagName: string,
		targetDepartmentId?: string
	): Promise<void> {
		const currentDepartment = Services.Department.current;
		if (currentDepartment == null) {
			throw new Error('Cannot move collections without a current department');
		}

		let toDepartment: DepartmentJSONObject;
		if (targetDepartmentId != null && targetDepartmentId !== currentDepartment.objectId) {
			toDepartment = await Services.Department.departments.find((d) => d.objectId === targetDepartmentId);
			if (toDepartment == null) {
				throw new Error('Cannot move collections without a destination department');
			}
		}

		const allSelectors: CollectionSelector[] = collectionSummaries.map((summary) => ({ uuid: summary.uuid }));
		const tagIds = this.getTagIds(collectionSummaries);

		try {
			const startTime = Date.now();

			// Batch modifications to avoid hitting server limit
			const selectorBatches = IkeArray.chunk(allSelectors, CollectionService.BATCH_SIZE);
			for (const selectors of selectorBatches) {
				if (targetDepartmentId == null || targetDepartmentId === currentDepartment.objectId) {
					await Services.Department.rest.Collection.moveToJob({ collections: selectors, tagName });
				} else {
					await Services.Department.rest.Collection.moveToDepartment({
						collections: selectors,
						targetDepartmentId,
					});
				}
			}

			await Services.Tag.refresh(tagIds);
			CurrentState.Tag.reset();
			CurrentState.setInspectedCollection(null);

			AppStore.TagSummary.clear();

			const label =
				toDepartment != null ? `"${currentDepartment.displayName}" -> "${toDepartment.displayName}"` : tagName;
			Services.Analytics.trackEvent(
				Services.Analytics.EventCategory.BigMap,
				'COLLECTION_MOVE',
				label,
				Date.now() - startTime
			);
		} catch (e) {
			handleGenericError(e);
		}
	}

	async save(collection: CollectionModel, modifications?: Modifications): Promise<CollectionJSONObject> {
		const rest = Services.Department.rest;
		const collectionJSON = await IkeModel.Collection.write(collection);
		const newCollectionJSON = await rest.save(rest.Objects.Collection, collectionJSON, modifications);
		await rest.CollectionSummary.create({ uuid: newCollectionJSON.uuid, repair: true });

		Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'Collection:save', collection.uuid, 1);

		await this.refresh([newCollectionJSON.uuid]);
		await AppStore.Collection.fetch(newCollectionJSON.revision);

		return newCollectionJSON;
	}

	private async refresh(collectionUuids: string[]) {
		const collectionSummaries = await Promise.all(
			collectionUuids.map((uuid) => AppStore.CollectionSummary.getFetchableByUuid(uuid).refetch())
		);
		return Promise.all([
			AppStore.Tag.refresh(),
			...this.getTagIds(collectionSummaries).map((tagId) => AppStore.TagSummary.getFetchable(tagId).refetch()),
		]);
	}

	private getTagIds(collectionSummaries: CollectionSummaryJSONObject[]): string[] {
		const tags: { [id: string]: boolean } = {};

		for (const collectionSummary of collectionSummaries) {
			for (const tag of collectionSummary.tags) {
				if (!tags[tag] && !isSystemTag(tag)) {
					tags[tag] = true;
				}
			}
		}

		return Object.keys(tags);
	}
}
